import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Button, Pagination, DateRangePicker } from 'components/common';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { MdOutlineCalendarMonth, MdFilterAlt } from 'react-icons/md';
import { FaSearch, FaSyncAlt, FaTimes, FaEdit, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { RiSecurePaymentFill } from 'react-icons/ri';
import { getProjectSchedules, exportData as exportSchedules, setNestedParams } from 'redux/crm/project/scheduleSlice';
import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { DateFormatRenderer } from 'components/common/ag-grid';
import StatusRenderer from 'components/app/crm/project-schedule-pmd/StatusRenderer';
import DurationRenderer from 'components/app/crm/project-schedule-pmd/DurationRenderer';
import PaymentPlanFormatRenderer from 'components/app/crm/project-schedule-pmd/PaymentPlanFormatRenderer';
import ProjectScheduleForm from 'components/app/crm/project-schedule-pmd/ProjectScheduleForm';
import ProjectSchedulePaymentModal from 'components/app/crm/project-schedule-pmd/ProjectSchedulePaymentModal';
import AdvanceFilterDrawer, { getEmptyValues } from 'components/common/AdvanceFilterDrawer';
import { debounce } from 'helpers/debounce';
import 'components/app/crm/project-schedule-pmd/style.css';

// Register Enterprise (includes all Community modules)
ModuleRegistry.registerModules([AllEnterpriseModule]);

// ── Constants ────────────────────────────────────────────────────────────────
const SEARCH_FIELD = 'project_q_number_or_building_j_number_or_project_name_or_name_cont';

const FILTER_FIELDS = [
  { key: 'quote_number',   label: 'Quote Number',   type: 'text', paramKey: 'project_q_number_cont',       tagName: 'Q#' },
  { key: 'job_number',     label: 'J Number',       type: 'text', paramKey: 'building_j_number_cont',      tagName: 'J#' },
  { key: 'project_name',   label: 'Project Name',   type: 'text', paramKey: 'project_name_cont',           tagName: 'Project' },
  { key: 'building_name',  label: 'Building Name',  type: 'text', paramKey: 'building_name_cont',          tagName: 'Building' },
  { key: 'phase_name',     label: 'Phase',          type: 'text', paramKey: 'name_cont',                   tagName: 'Phase' },
  { key: 'sales_incharge', label: 'Sales Incharge', type: 'text', paramKey: 'project_sales_incharge_cont', tagName: 'Sales' },
];

const pad = (n) => String(n).padStart(2, '0');
const _now = new Date();
const THIS_MONTH = {
  startDate: `${_now.getFullYear()}-${pad(_now.getMonth() + 1)}-01`,
  endDate:   `${_now.getFullYear()}-${pad(_now.getMonth() + 1)}-${pad(_now.getDate())}`,
};

const FIXED_COL_DEF = { flex: undefined, minWidth: 60, resizable: true, sortable: false, filter: false };

// ── Group row renderer (project & building headers) ───────────────────────────
// Rendered as full-width rows; individual column cells are NOT used for group rows.
const GroupRowRenderer = (params) => {
  const { node, context } = params;
  const { handleEdit, handlePayment } = context || {};
  const leafData = node.allLeafChildren?.[0]?.data || {};
  const { project, building, permissions } = leafData;

  if (node.level === 0) {
    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
          height: '100%', background: '#1e3a5f', color: 'white', fontWeight: 600, cursor: 'pointer',
        }}
        onClick={() => node.setExpanded(!node.expanded)}
      >
        <span style={{ fontSize: 11, opacity: 0.8 }}>
          {node.expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
        </span>
        <Link
          to={`/crm/projects/${project?.id}`}
          target="_blank"
          style={{ color: '#93c5fd', fontWeight: 700, textDecoration: 'none', flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {building?.job_number}
        </Link>
        <span style={{ color: '#bfdbfe', flexShrink: 0 }}>{project?.quote_number}</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {project?.name}
        </span>
        {project?.sales_incharge && (
          <span style={{ color: '#bfdbfe', fontSize: 11, flexShrink: 0 }}>
            {project.sales_incharge}
          </span>
        )}
      </div>
    );
  }

  if (node.level === 1) {
    // const contract    = building?.contract || {};
    // const paymentTerm = building?.payment_term || {};
    // const radActual   = building?.rad_actual_date;
    const { allow_edit, allow_view_payment_plan } = permissions || {};

    // const info = [
    //   contract.actual_date && `Contract: ${formatDate(contract.actual_date)}`,
    //   contract.status,
    //   paymentTerm.description,
    //   paymentTerm.received_up_to_date != null && `${paymentTerm.received_up_to_date}% recv`,
    //   radActual && `RAD: ${formatDate(radActual)}`,
    //   building?.cse_order_entry,
    //   building?.cse_order_management,
    //   sales_unit?.name,
    //   sales_unit?.region,
    // ].filter(Boolean);

    return (
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px 0 40px',
          height: '100%', background: '#dbeafe', borderBottom: '1px solid #93c5fd', cursor: 'pointer',
        }}
        onClick={() => node.setExpanded(!node.expanded)}
      >
        <span style={{ fontSize: 10, color: '#1e40af' }}>
          {node.expanded ? <FaChevronDown size={9} /> : <FaChevronRight size={9} />}
        </span>
        <strong style={{ minWidth: 36, color: '#1e40af', flexShrink: 0 }}>{building?.name}</strong>
        <span
          style={{
            flex: 1, fontSize: 12, color: '#374151',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {/* {info.join('  ·  ')} */}
        </span>
        <span style={{ display: 'flex', gap: 10, marginLeft: 8, flexShrink: 0 }}>
          {allow_edit && (
            <span
              style={{ color: '#3b82f6', cursor: 'pointer' }}
              title="Edit schedule"
              onClick={(e) => { e.stopPropagation(); handleEdit?.(leafData); }}
            >
              <FaEdit size={13} />
            </span>
          )}
          {allow_view_payment_plan && (
            <span
              style={{ color: '#f59e0b', cursor: 'pointer' }}
              title="Payment plan"
              onClick={(e) => { e.stopPropagation(); handlePayment?.(leafData); }}
            >
              <RiSecurePaymentFill size={14} />
            </span>
          )}
        </span>
      </div>
    );
  }

  return null;
};

// ── Sales Unit picker ─────────────────────────────────────────────────────────
const SalesUnitPicker = ({ groups, value, onChange }) => {
  const [open, setOpen]         = useState(false);
  const [pos, setPos]           = useState({ top: 0, left: 0 });
  const [expanded, setExpanded] = useState({});
  const triggerRef              = useRef(null);
  const dropdownRef             = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))
      ) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleToggleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
      if (Object.keys(expanded).length === 0) {
        const all = {};
        groups.forEach((g) => { all[g.region] = true; });
        setExpanded(all);
      }
    }
    setOpen((o) => !o);
  };

  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const toggleRegion = (units) => {
    const ids = units.map((u) => u.value);
    const allSel = ids.every((id) => value.includes(id));
    onChange(allSel ? value.filter((v) => !ids.includes(v)) : [...new Set([...value, ...ids])]);
  };

  const isActive = value.length > 0;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggleOpen}
        className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-cursor-pointer tw-transition-all tw-whitespace-nowrap tw-flex-shrink-0 ${
          isActive
            ? 'tw-bg-primary-50 tw-border-primary-300 tw-text-primary-700'
            : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
        }`}
      >
        <span>{isActive ? `${value.length} unit${value.length > 1 ? 's' : ''}` : 'Sales Unit'}</span>
        {isActive && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onChange([]); }}
            onKeyDown={(e) => e.key === 'Enter' && onChange([])}
            className="tw-flex tw-items-center tw-justify-center tw-w-3.5 tw-h-3.5 tw-rounded-xl tw-bg-primary-200 hover:tw-bg-red-200 tw-cursor-pointer tw-transition-colors"
          >
            <FaTimes size={7} className="tw-text-primary-700" />
          </span>
        )}
      </button>

      {open && ReactDOM.createPortal(
        <>
          <div className="tw-fixed tw-inset-0 tw-z-[9998]" onClick={() => setOpen(false)} />
          <div
            ref={dropdownRef}
            className="tw-fixed tw-z-[9999] tw-bg-white tw-rounded tw-shadow-xl tw-border tw-border-slate-200 tw-py-1 tw-min-w-[220px] tw-max-h-80 tw-overflow-y-auto"
            style={{ top: pos.top, left: pos.left }}
          >
            {groups.map((group) => {
              const ids    = group.units.map((u) => u.value);
              const allChk = ids.length > 0 && ids.every((id) => value.includes(id));
              const someChk = ids.some((id) => value.includes(id));
              const isExp  = expanded[group.region] !== false;
              return (
                <div key={group.region}>
                  <div className="tw-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1.5 tw-bg-slate-50 tw-border-b tw-border-slate-100">
                    <button
                      type="button"
                      onClick={() => setExpanded((p) => ({ ...p, [group.region]: !isExp }))}
                      className="tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-text-slate-400 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-flex-shrink-0"
                    >
                      <FaChevronRight size={8} className={`tw-transition-transform ${isExp ? 'tw-rotate-90' : ''}`} />
                    </button>
                    <input
                      type="checkbox"
                      checked={allChk}
                      ref={(el) => { if (el) el.indeterminate = !allChk && someChk; }}
                      onChange={() => toggleRegion(group.units)}
                      className="tw-accent-primary-600 tw-w-3.5 tw-h-3.5 tw-flex-shrink-0"
                    />
                    <span className="tw-text-xs tw-font-semibold tw-text-slate-500 tw-uppercase tw-tracking-wide tw-flex-1">{group.region}</span>
                    <span className="tw-text-xs tw-text-slate-400">{group.units.length}</span>
                  </div>
                  {isExp && group.units.map((unit) => (
                    <label
                      key={unit.value}
                      className="tw-flex tw-items-center tw-gap-2.5 tw-pl-8 tw-pr-3 tw-py-1.5 hover:tw-bg-primary-50 tw-cursor-pointer tw-text-sm tw-text-slate-700 tw-select-none"
                    >
                      <input
                        type="checkbox"
                        checked={value.includes(unit.value)}
                        onChange={() => toggle(unit.value)}
                        className="tw-accent-primary-600 tw-w-3.5 tw-h-3.5 tw-flex-shrink-0"
                      />
                      <span className="tw-text-xs">{unit.label}</span>
                    </label>
                  ))}
                </div>
              );
            })}
            {value.length > 0 && (
              <>
                <div className="tw-h-px tw-bg-slate-100 tw-mx-2 tw-my-1" />
                <button
                  type="button"
                  onClick={() => { onChange([]); setOpen(false); }}
                  className="tw-w-full tw-text-left tw-px-3 tw-py-1.5 tw-text-xs tw-text-red-500 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
        </>,
        document.body,
      )}
    </>
  );
};

SalesUnitPicker.propTypes = {
  groups:   PropTypes.arrayOf(PropTypes.shape({
    region: PropTypes.string,
    units:  PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number, label: PropTypes.string })),
  })).isRequired,
  value:    PropTypes.arrayOf(PropTypes.number).isRequired,
  onChange: PropTypes.func.isRequired,
};

// ── Column definitions (phase-level data only) ────────────────────────────────
// Building/project data is shown in the group row headers (GroupRowRenderer).
const COLUMN_DEFS = [
  // Hidden virtual columns used only for row grouping
  { field: '_projectKey', rowGroup: true, hide: true },
  { field: '_buildingKey', rowGroup: true, hide: true },

  // ── Phase identity ────────────────────────────────────────────────────────
  {
    headerName: '',
    children: [
      {
        headerName: 'Phase',
        field: 'name',
        width: 120,
        pinned: 'left',
        cellRenderer: (params) => {
          const data = params.data || {};
          if (!data.name) return null;
          return (
            <span className="tw-text-blue-600">
              {data.name}{data.description ? `-${data.description}` : ''}
            </span>
          );
        },
      },
      { headerName: 'Remark',             field: 'pmd_remark',    width: 140 },
      { headerName: 'CSD requested Shop', field: 'shop_requested', width: 120, cellRenderer: DateFormatRenderer },
    ],
  },

  // ── Payment plan (phase-level entries) ────────────────────────────────────
  {
    headerName: 'Payment plan',
    children: [
      { headerName: 'Plan',   field: 'payment_term.plan_date',   width: 140, cellRenderer: PaymentPlanFormatRenderer, cellClass: 'payment-color' },
      { headerName: 'Actual', field: 'payment_term.actual_date', width: 140, cellRenderer: PaymentPlanFormatRenderer, cellClass: 'payment-color' },
    ],
  },

  // ── APD ───────────────────────────────────────────────────────────────────
  {
    headerName: 'APD',
    children: [
      { headerName: 'Submission', field: 'apd.submission_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'apd-color' },
    ],
  },

  // ── RAD ───────────────────────────────────────────────────────────────────
  {
    headerName: 'RAD',
    children: [
      { headerName: 'Plan', field: 'rad.plan_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'rad-color' },
    ],
  },

  // ── SHOP Drawing ─────────────────────────────────────────────────────────
  {
    headerName: 'SHOP Drawing (FABRICATION)',
    children: [
      { headerName: 'Plan',   field: 'shop_drawing.scheduled', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'shop-color' },
      { headerName: 'Actual', field: 'shop_drawing.released',  width: 120, cellRenderer: DateFormatRenderer, cellClass: 'shop-color' },
      { headerName: 'Status', field: 'shop_drawing.status',    width: 100, cellRenderer: StatusRenderer,     cellClass: 'shop-color' },
    ],
  },

  // ── Erection Drawing ─────────────────────────────────────────────────────
  {
    headerName: 'ERECTION DRAWING',
    children: [
      { headerName: 'Plan',   field: 'erection_drawing.scheduled', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'shop-color' },
      { headerName: 'Actual', field: 'erection_drawing.released',  width: 120, cellRenderer: DateFormatRenderer, cellClass: 'shop-color' },
      { headerName: 'Status', field: 'erection_drawing.status',    width: 100, cellRenderer: StatusRenderer,     cellClass: 'shop-color' },
    ],
  },

  // ── Procurement ───────────────────────────────────────────────────────────
  {
    headerName: 'PROCUREMENT MATERIAL',
    children: [
      {
        headerName: 'Duration',
        field: 'procurement_material_duration',
        width: 150,
        cellRenderer: (p) => <span className="tw-text-blue-600">{p.value}</span>,
        cellClass: 'procurement-color',
      },
    ],
  },

  // ── Fabrication ───────────────────────────────────────────────────────────
  {
    headerName: 'Fabrication PLAN at FACTORY',
    children: [
      { headerName: 'Plan',     field: 'fabrication_at_factory.plan_date',   width: 120, cellRenderer: DateFormatRenderer, cellClass: 'fabrication-color' },
      { headerName: 'Actual',   field: 'fabrication_at_factory.actual_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'fabrication-color' },
      { headerName: 'Duration', field: 'fabrication_at_factory.duration',    width: 100, cellRenderer: DurationRenderer,   cellClass: 'fabrication-color' },
    ],
  },

  // ── Delivery ─────────────────────────────────────────────────────────────
  {
    headerName: 'Delivery plan',
    children: [
      { headerName: 'Plan',     field: 'delivery.plan_date',   width: 120, cellRenderer: DateFormatRenderer, cellClass: 'delivery-color' },
      { headerName: 'Actual',   field: 'delivery.actual_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'delivery-color' },
      { headerName: 'Duration', field: 'delivery.duration',    width: 100, cellRenderer: DurationRenderer,   cellClass: 'delivery-color' },
    ],
  },

  // ── Erection at Site ─────────────────────────────────────────────────────
  {
    headerName: 'ERECTION AT SITE (CMD)',
    children: [
      { headerName: 'Plan',     field: 'erection_at_site.plan_date',   width: 120, cellRenderer: DateFormatRenderer, cellClass: 'erection-color' },
      { headerName: 'Actual',   field: 'erection_at_site.actual_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'erection-color' },
      { headerName: 'Duration', field: 'erection_at_site.duration',    width: 100, cellRenderer: DurationRenderer,   cellClass: 'erection-color' },
    ],
  },

  // ── Punch List ────────────────────────────────────────────────────────────
  {
    headerName: 'PUNCH LIST',
    children: [
      { headerName: 'Plan',     field: 'punch_list.plan_date',     width: 120, cellRenderer: DateFormatRenderer, cellClass: 'punchlist-color' },
      { headerName: 'Expected', field: 'punch_list.expected_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'punchlist-color' },
      { headerName: 'Actual',   field: 'punch_list.actual_date',   width: 120, cellRenderer: DateFormatRenderer, cellClass: 'punchlist-color' },
    ],
  },

  // ── Hand Over ─────────────────────────────────────────────────────────────
  {
    headerName: 'HAND OVER',
    children: [
      { headerName: 'Expected', field: 'hand_over.expected_date', width: 120, cellRenderer: DateFormatRenderer, cellClass: 'handover-color' },
      { headerName: 'Actual',   field: 'hand_over.actual_date',   width: 120, cellRenderer: DateFormatRenderer, cellClass: 'handover-color' },
      { headerName: 'Status',   field: 'hand_over.status',        width: 100, cellRenderer: StatusRenderer,     cellClass: 'handover-color' },
    ],
  },
];

// ── Main page ────────────────────────────────────────────────────────────────
const ProjectSchedulePMDPage = () => {
  const dispatch = useDispatch();

  const rawRowData                        = useSelector((s) => s.crm_project_schedule_pmd.allData.data);
  const totalRows                         = useSelector((s) => s.crm_project_schedule_pmd.allData.totalRows);
  const loading                           = useSelector((s) => s.crm_project_schedule_pmd.loading);
  const exporting                         = useSelector((s) => s.crm_project_schedule_pmd.exporting);
  const { activeSalesUnitsData = [] }     = useSelector((s) => s.crm_sales_unit);

  // Modals
  const [openForm, setOpenForm]               = useState(false);
  const [openPaymentForm, setOpenPaymentForm] = useState(false);
  const [record, setRecord]                   = useState(null);

  const handleEdit    = useCallback((rec) => { setRecord(rec); setOpenForm(true); }, []);
  const handlePayment = useCallback((rec) => { setRecord(rec); setOpenPaymentForm(true); }, []);

  // Stable context passed to GroupRowRenderer (state setters are stable, so [] is fine)
  const gridContext = useMemo(() => ({ handleEdit, handlePayment }), [handleEdit, handlePayment]);

  // Attach virtual grouping keys to each row
  const rowData = useMemo(() =>
    (rawRowData || []).map((row) => ({
      ...row,
      _projectKey:  String(row.project?.id ?? ''),
      _buildingKey: `${row.project?.id}__${row.building?.name ?? row.building?.id ?? ''}`,
    })),
    [rawRowData],
  );

  const getRowHeight = useCallback((params) => {
    if (params.node?.level === 0) return 42;  // project header
    if (params.node?.level === 1) return 38;  // building header
    return 35;                                // phase row
  }, []);

  // Sales units grouped by region
  const salesUnitGroups = useMemo(() => {
    const grouped = {};
    activeSalesUnitsData.forEach((u) => {
      const region = u.region || 'Other';
      if (!grouped[region]) grouped[region] = [];
      grouped[region].push({ value: u.id, label: u.description || u.name || String(u.id) });
    });
    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([region, units]) => ({ region, units }));
  }, [activeSalesUnitsData]);

  // ── Pagination / filter state ─────────────────────────────────────────────
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [sort, setSort]       = useState({ attribute: 'id', direction: 'desc' });
  const [search, setSearch]   = useState('');
  const [filterValues, setFilterValues] = useState(() => getEmptyValues(FILTER_FIELDS));
  const [filterTags, setFilterTags]     = useState([]);
  const [showFilter, setShowFilter]     = useState(false);

  // ── Quick filter state ────────────────────────────────────────────────────
  const [quickContractDate, setQuickContractDate] = useState({ startDate: '', endDate: '' });
  const [quickCsdShop,      setQuickCsdShop]      = useState({ startDate: '', endDate: '' });
  const [quickRad,          setQuickRad]          = useState({ startDate: '', endDate: '' });
  const [quickSalesUnits,   setQuickSalesUnits]   = useState([]);
  const [quickCreatedDate,  setQuickCreatedDate]  = useState(THIS_MONTH);

  const activeFilterParamsRef = useRef({});
  const searchRef             = useRef('');
  const quickFiltersRef       = useRef({
    contractDate: { startDate: '', endDate: '' },
    csdShop:      { startDate: '', endDate: '' },
    rad:          { startDate: '', endDate: '' },
    salesUnits:   [],
    createdDate:  THIS_MONTH,
  });

  const totalPages = Math.ceil(totalRows / perPage) || 1;

  const buildQuickFilterParams = () => {
    const { contractDate, csdShop, rad, salesUnits, createdDate } = quickFiltersRef.current;
    const p = {};
    if (contractDate.startDate)  p.building_contract_date_gteq              = contractDate.startDate;
    if (contractDate.endDate)    p.building_contract_date_lteq              = contractDate.endDate;
    if (csdShop.startDate)       p.shop_requested_gteq                      = csdShop.startDate;
    if (csdShop.endDate)         p.shop_requested_lteq                      = csdShop.endDate;
    if (rad.startDate)           p.building_return_approval_drawing_gteq    = rad.startDate;
    if (rad.endDate)             p.building_return_approval_drawing_lteq    = rad.endDate;
    if (salesUnits.length > 0)   p.sales_unit_id_in                         = salesUnits;
    if (createdDate.startDate)   p.schedule_created_at_gteq                 = createdDate.startDate;
    if (createdDate.endDate)     p.schedule_created_at_lteq                 = createdDate.endDate;
    return p;
  };

  const buildParams = useCallback(
    (overrides = {}) => ({
      page, per_page: perPage,
      ...(sort.attribute && { sort }),
      ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }),
      ...buildQuickFilterParams(),
      ...activeFilterParamsRef.current,
      ...overrides,
    }),
    [page, perPage, sort],
  );

  const fetchData = useCallback((params) => {
    dispatch(setNestedParams({ key: 'allData', params: { page: params.page, per_page: params.per_page } }));
    dispatch(getProjectSchedules(params));
  }, [dispatch]);

  useEffect(() => { dispatch(getActiveSalesUnits()); }, [dispatch]);
  useEffect(() => { fetchData(buildParams()); }, [fetchData, buildParams]);

  const debouncedFetch = useRef(debounce((params) => fetchData(params), 500)).current;

  // ── Event handlers ────────────────────────────────────────────────────────
  const handleSearchChange = (e) => {
    const val = e.target.value;
    searchRef.current = val;
    setSearch(val);
    setPage(1);
    debouncedFetch({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(val && { [SEARCH_FIELD]: val }), ...buildQuickFilterParams(), ...activeFilterParamsRef.current });
  };

  const handleClearSearch = () => {
    searchRef.current = '';
    setSearch('');
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...buildQuickFilterParams(), ...activeFilterParamsRef.current });
  };

  const handleSort = useCallback(({ colId, sort: dir }) => {
    const newSort = dir ? { attribute: colId, direction: dir } : { attribute: 'id', direction: 'desc' };
    setSort(newSort);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, sort: newSort, ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickFilterParams(), ...activeFilterParamsRef.current });
  }, [fetchData, perPage]);

  const handleApplyFilter = (params, tags) => {
    activeFilterParamsRef.current = params;
    setFilterTags(tags);
    setPage(1);
    setShowFilter(false);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickFilterParams(), ...params });
  };

  const handleResetFilter = () => {
    activeFilterParamsRef.current = {};
    quickFiltersRef.current = {
      contractDate: { startDate: '', endDate: '' },
      csdShop:      { startDate: '', endDate: '' },
      rad:          { startDate: '', endDate: '' },
      salesUnits:   [],
      createdDate:  { startDate: '', endDate: '' },
    };
    setFilterValues(getEmptyValues(FILTER_FIELDS));
    setFilterTags([]);
    setQuickContractDate({ startDate: '', endDate: '' });
    setQuickCsdShop({ startDate: '', endDate: '' });
    setQuickRad({ startDate: '', endDate: '' });
    setQuickSalesUnits([]);
    setQuickCreatedDate({ startDate: '', endDate: '' });
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }) });
  };

  const DATE_RANGE_PAIRS = {
    building_contract_date_gteq:           ['building_contract_date_gteq', 'building_contract_date_lteq'],
    building_contract_date_lteq:           ['building_contract_date_gteq', 'building_contract_date_lteq'],
    shop_requested_gteq:                   ['shop_requested_gteq', 'shop_requested_lteq'],
    shop_requested_lteq:                   ['shop_requested_gteq', 'shop_requested_lteq'],
    building_return_approval_drawing_gteq: ['building_return_approval_drawing_gteq', 'building_return_approval_drawing_lteq'],
    building_return_approval_drawing_lteq: ['building_return_approval_drawing_gteq', 'building_return_approval_drawing_lteq'],
    schedule_created_at_gteq:              ['schedule_created_at_gteq', 'schedule_created_at_lteq'],
    schedule_created_at_lteq:             ['schedule_created_at_gteq', 'schedule_created_at_lteq'],
  };

  const removeTag = (tag) => {
    const keysToRemove = new Set(DATE_RANGE_PAIRS[tag.id] || [tag.id]);
    const newParams = Object.fromEntries(Object.entries(activeFilterParamsRef.current).filter(([k]) => !keysToRemove.has(k)));
    activeFilterParamsRef.current = newParams;
    setFilterTags((prev) => prev.filter((t) => t.id !== tag.id));
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickFilterParams(), ...newParams });
  };

  const mkDateHandler = (refKey, setter) => ({ startDate, endDate }) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, [refKey]: { startDate, endDate } };
    setter({ startDate, endDate });
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickFilterParams(), ...activeFilterParamsRef.current });
  };

  const handleQuickContractDateChange = mkDateHandler('contractDate', setQuickContractDate);
  const handleQuickCsdShopChange      = mkDateHandler('csdShop',      setQuickCsdShop);
  const handleQuickRadChange          = mkDateHandler('rad',          setQuickRad);
  const handleQuickCreatedDateChange  = mkDateHandler('createdDate',  setQuickCreatedDate);

  const handleQuickSalesUnitsChange = (ids) => {
    quickFiltersRef.current = { ...quickFiltersRef.current, salesUnits: ids };
    setQuickSalesUnits(ids);
    setPage(1);
    fetchData({ page: 1, per_page: perPage, ...(sort.attribute && { sort }), ...(searchRef.current && { [SEARCH_FIELD]: searchRef.current }), ...buildQuickFilterParams(), ...activeFilterParamsRef.current });
  };

  const handleGridSort = useCallback((event) => {
    const model = event.api.getSortModel ? event.api.getSortModel() : [];
    if (handleSort) handleSort(model.length > 0 ? model[0] : { colId: null, sort: null });
  }, [handleSort]);

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="tw-flex tw-flex-col tw-gap-3 tw-p-4">

      {/* Header */}
      <div className="tw-mb-1">
        <div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            <div className="tw-w-1.5 tw-h-10 tw-rounded-sm tw-flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #6366f1)' }} />
            <div className="tw-flex tw-items-center tw-gap-2">
              <MdOutlineCalendarMonth className="tw-text-blue-600" size={22} />
              <div>
                <h1 className="tw-mb-0 tw-font-bold tw-text-2xl tw-text-gray-900">Project Schedule (PMD)</h1>
                <p className="tw-text-gray-500 tw-text-sm tw-mb-0">Engineering schedule tracking for Project Management Department.</p>
              </div>
            </div>
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Button variant="secondary" size="md" leftIcon={FaSyncAlt} loading={loading} onClick={() => fetchData(buildParams())}>
              Reload
            </Button>
            <Button variant="secondary" size="md" leftIcon={FaFileExcel} disabled={exporting || loading} onClick={() => dispatch(exportSchedules(buildParams()))} className="tw-text-green-700 tw-border-green-300 tw-bg-green-50 hover:tw-bg-green-100">
              {exporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2.5">
          <div className="tw-relative tw-flex tw-items-center tw-w-full sm:tw-w-52 sm:tw-flex-shrink-0">
            <FaSearch className="tw-absolute tw-left-3 tw-text-slate-400 tw-pointer-events-none" size={12} />
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Q#, J#, project or phase..."
              className="tw-w-full tw-pl-8 tw-pr-7 tw-py-1.5 tw-text-sm tw-border tw-border-slate-200 tw-rounded focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary-400 focus:tw-border-transparent tw-bg-slate-50 hover:tw-bg-white tw-transition-colors tw-text-slate-700 tw-placeholder-slate-400"
            />
            {search && (
              <button type="button" onClick={handleClearSearch} className="tw-absolute tw-right-2 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-xl tw-bg-slate-300 hover:tw-bg-slate-400 tw-text-white tw-border-0 tw-cursor-pointer tw-transition-colors tw-p-0">
                <FaTimes size={8} />
              </button>
            )}
          </div>
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <div className="tw-flex-shrink-0"><DateRangePicker label="Contract Date" startDate={quickContractDate.startDate} endDate={quickContractDate.endDate} onChange={handleQuickContractDateChange} /></div>
          <div className="tw-flex-shrink-0"><DateRangePicker label="CSD Req. Shop" startDate={quickCsdShop.startDate} endDate={quickCsdShop.endDate} onChange={handleQuickCsdShopChange} /></div>
          <div className="tw-flex-shrink-0"><DateRangePicker label="RAD" startDate={quickRad.startDate} endDate={quickRad.endDate} onChange={handleQuickRadChange} /></div>
          {salesUnitGroups.length > 0 && (
            <SalesUnitPicker groups={salesUnitGroups} value={quickSalesUnits} onChange={handleQuickSalesUnitsChange} />
          )}
          <div className="tw-flex-shrink-0"><DateRangePicker label="Created Date" startDate={quickCreatedDate.startDate} endDate={quickCreatedDate.endDate} onChange={handleQuickCreatedDateChange} /></div>
          <div className="tw-hidden sm:tw-block tw-h-5 tw-w-px tw-bg-slate-200 tw-flex-shrink-0" />
          <button
            type="button"
            onClick={() => setShowFilter(true)}
            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded tw-border tw-text-sm tw-font-medium tw-cursor-pointer tw-transition-all tw-flex-shrink-0 ${
              filterTags.length > 0
                ? 'tw-bg-primary-600 tw-border-primary-600 tw-text-white hover:tw-bg-primary-700'
                : 'tw-bg-white tw-border-slate-200 tw-text-slate-600 hover:tw-border-primary-300 hover:tw-text-primary-600 hover:tw-bg-primary-50'
            }`}
          >
            <MdFilterAlt size={15} />
            More Filters
            {filterTags.length > 0 && (
              <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[18px] tw-h-[18px] tw-px-1 tw-rounded-xl tw-bg-white/30 tw-text-white tw-text-xs tw-font-bold tw-leading-none">
                {filterTags.length}
              </span>
            )}
          </button>
          {!loading && totalRows > 0 && (
            <span className="tw-ml-auto tw-flex-shrink-0 tw-text-xs tw-text-slate-400 tw-font-medium">
              {totalRows.toLocaleString()} records
            </span>
          )}
        </div>

        {filterTags.length > 0 && (
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-border-t tw-border-slate-100 tw-bg-primary-50/40">
            <span className="tw-text-xs tw-text-slate-400 tw-font-medium tw-flex-shrink-0">Active filters:</span>
            {filterTags.map((tag) => (
              <span key={tag.id} className="tw-inline-flex tw-items-center tw-gap-1 tw-pl-2.5 tw-pr-1 tw-py-0.5 tw-bg-white tw-border tw-border-primary-200 tw-text-slate-700 tw-rounded-md tw-text-xs tw-shadow-sm">
                <span className="tw-text-primary-500 tw-font-semibold">{tag.name}</span>
                <span className="tw-text-slate-300 tw-mx-0.5">·</span>
                <span className="tw-max-w-[140px] tw-truncate tw-text-slate-600">{Array.isArray(tag.value) ? tag.value.join(' – ') : tag.value}</span>
                <button type="button" onClick={() => removeTag(tag)} className="tw-ml-0.5 tw-flex tw-items-center tw-justify-center tw-w-4 tw-h-4 tw-rounded-sm tw-text-slate-400 hover:tw-bg-red-100 hover:tw-text-red-500 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors">
                  <FaTimes size={8} />
                </button>
              </span>
            ))}
            <Button variant="secondary" size="xs" leftIcon={FaTimes} onClick={handleResetFilter} className="tw-text-red-500 tw-border-red-200 tw-bg-transparent hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500">
              Clear all
            </Button>
          </div>
        )}
      </div>

      {/* Grid */}
      <div className="tw-bg-white tw-rounded tw-shadow-sm tw-border tw-border-slate-200 tw-overflow-hidden">
        {loading && (
          <div className="tw-flex tw-items-center tw-justify-center tw-p-3 tw-bg-blue-50 tw-border-b tw-border-blue-200">
            <div className="tw-animate-spin tw-rounded-xl tw-h-4 tw-w-4 tw-border-t-2 tw-border-b-2 tw-border-blue-500 tw-mr-2" />
            <span className="tw-text-sm tw-text-blue-700">Loading...</span>
          </div>
        )}
        <div
          className="ag-theme-quartz"
          style={{ height: 'max(320px, calc(100dvh - 290px))', '--ag-font-size': '13px', '--ag-font-family': 'inherit' }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={COLUMN_DEFS}
            defaultColDef={FIXED_COL_DEF}

            // Row grouping (Enterprise)
            groupDisplayType="groupRows"
            groupDefaultExpanded={-1}
            groupRowRenderer={GroupRowRenderer}
            suppressAggFuncInHeader
            context={gridContext}

            // Layout
            rowHeight={35}
            headerHeight={45}
            headerGroupHeight={45}
            getRowHeight={getRowHeight}
            domLayout={undefined}

            // Pagination suppressed (handled externally)
            pagination={false}

            // Sort
            onSortChanged={handleGridSort}
          />
        </div>
      </div>

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRows}
        perPage={perPage}
        pageSizeOptions={[20, 50, 100, 200]}
        onPageChange={setPage}
        onPageSizeChange={(s) => { setPerPage(s); setPage(1); }}
        disabled={loading}
      />

      <AdvanceFilterDrawer
        show={showFilter}
        onClose={() => setShowFilter(false)}
        onApply={handleApplyFilter}
        onReset={handleResetFilter}
        fields={FILTER_FIELDS}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        title="Filter Project Schedule (PMD)"
      />

      {openForm && (
        <ProjectScheduleForm open={openForm} setOpen={setOpenForm} record={record} />
      )}
      {openPaymentForm && (
        <ProjectSchedulePaymentModal open={openPaymentForm} setOpen={setOpenPaymentForm} projectId={record?.project?.id} />
      )}
    </div>
  );
};

export default ProjectSchedulePMDPage;
